import { useCallback, useEffect, type FC } from 'react';
import { useSurfacingExperimentContext } from '../../contexts';
import { useGetStatsigEnrollment } from '../hooks/useGetStatsigVariant';

import { SURFACING_RATING_EXPERIMENT } from '../../experimentation/constants';

import { getRatingsData } from '../RelatedEvents/api';
import { isRatingsCriteriaMatching } from '../_shared/OrganizerInfo/helperFunctions';

import type { TSurfacingExperimentVariant } from '../../contexts';
import type { IRatingResponse } from '../RelatedEvents/types/api';

interface ISurfacingExperimentDataHandlerProps {
    organizerId: string;
}

export const SurfacingExperimentDataHandler: FC<
    ISurfacingExperimentDataHandlerProps
> = ({ organizerId }) => {
    const {
        ratingsData,
        setVariant,
        setRatingsData,
        setShowOrganiserInfoLoader,
    } = useSurfacingExperimentContext();

    const { variant } = useGetStatsigEnrollment<TSurfacingExperimentVariant>({
        name: SURFACING_RATING_EXPERIMENT.experimentName,
        paramName: SURFACING_RATING_EXPERIMENT.param,
        defaultValue: SURFACING_RATING_EXPERIMENT.defaultValue,
    });

    const setControlVersion = useCallback(() => {
        setVariant('control');
        setShowOrganiserInfoLoader(false);
    }, [setShowOrganiserInfoLoader, setVariant]);

    const fetchRatingsData = useCallback(async () => {
        try {
            const ratingsData = await getRatingsData(organizerId);
            setRatingsData(ratingsData);
        } catch (e) {
            console.log('cannot fetch ratings data', e);
            setControlVersion();
        }
    }, [organizerId, setControlVersion, setRatingsData]);

    useEffect(() => {
        if (variant === 'variant') {
            fetchRatingsData();
        } else if (variant === 'control') {
            setControlVersion();
        }
    }, [variant, setControlVersion, fetchRatingsData]);

    const setVariantVersionSafe = useCallback(
        (ratingsData: IRatingResponse['ratings']) => {
            setVariant(
                isRatingsCriteriaMatching(ratingsData) ? 'variant' : 'control',
            );
            setShowOrganiserInfoLoader(false);
        },
        [setShowOrganiserInfoLoader, setVariant],
    );

    useEffect(() => {
        if (!ratingsData) return;
        setVariantVersionSafe(ratingsData);
    }, [setVariantVersionSafe, ratingsData]);

    return null;
};
